import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  GridColDef,
  GridColumnOrderChangeParams,
} from '@mui/x-data-grid-pro';

const syncOrder = (current: string[], nextFields: string[]) => {
  if (nextFields.length === 0) {
    return current;
  }

  const ordered = current.filter((field) => nextFields.includes(field));
  const missing = nextFields.filter((field) => !ordered.includes(field));
  const merged = [...ordered, ...missing];

  const sameOrder =
    merged.length === current.length &&
    merged.every((field, index) => field === current[index]);

  return sameOrder ? current : merged;
};

export const useColumnOrder = (columns: GridColDef[]) => {
  const [orderedFields, setOrderedFields] = useState<string[]>(() =>
    columns.map((column) => column.field),
  );

  useEffect(() => {
    setOrderedFields((prev) =>
      syncOrder(prev, columns.map((column) => column.field)),
    );
  }, [columns]);

  const orderedColumns = useMemo(() => {
    if (orderedFields.length === 0) {
      return columns;
    }

    const columnMap = new Map(columns.map((column) => [column.field, column]));
    const nextColumns: GridColDef[] = [];

    orderedFields.forEach((field) => {
      const column = columnMap.get(field);
      if (column) {
        nextColumns.push(column);
        columnMap.delete(field);
      }
    });

    columnMap.forEach((column) => nextColumns.push(column));

    return nextColumns;
  }, [columns, orderedFields]);

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      setOrderedFields((prev) => {
        const fields = prev.length
          ? [...prev]
          : columns.map((column) => column.field);

        if (fields.length === 0) {
          return prev;
        }

        const fromIndex = params.oldIndex;
        const toIndex = params.targetIndex;

        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
          return prev;
        }

        const [moved] = fields.splice(fromIndex, 1);
        fields.splice(toIndex, 0, moved);

        return fields;
      });
    },
    [columns],
  );

  return { orderedColumns, handleColumnOrderChange };
};
